// FamilienstandSection.tsx — Anzeige & Erfassung des Familienstands
// inklusive der ausklappbaren Informationen zur Hochzeit (standesamtlich/kirchlich
// und Mehrfach-Upload von Einladung/Mitteilung).

import * as React from "react";
import { FieldRow, LabeledInput, LabeledSelect } from "../forms/formPrimitives";
import {
  FAMILIENSTAND_OPTIONS,
  FAMILIENSTAND_SONSTIGES,
  FAMILIENSTAND_SONSTIGES_PLATZHALTER,
  familienstandAusFormular,
  familienstandAuswahlInsFormular,
  familienstandFreitextInsFormular,
} from "../../utils/familienstand";
import { REFINED } from "../risikoanalyseStep/tokens";
import { useRelStyles } from "./styles";
import { elementMitId } from "../windowManager/browserFenster/fensterRegister";

const UPLOAD_ICON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M10 12.5V3.5M6.5 7L10 3.5 13.5 7M4 15.5h12" />
  </svg>
);

const DOKUMENT_ICON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M5.5 2.5h6L15 6v11.5H5.5z" />
    <path d="M11.5 2.5V6H15" />
  </svg>
);

const ENTFERNEN_ICON = (
  <svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" style={{ flexShrink: 0 }}>
    <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
  </svg>
);

export interface HochzeitsDateiItem {
  id: string;
  name: string;
  file?: File;
}

export interface FamilienstandSectionProps {
  formFields: Record<string, string | number | boolean>;
  updateField: (name: string, value: string) => void;
  /** Signalisiert, dass im Haushalt eine Eheperson gewählt wurde → klappt Hochzeit automatisch auf */
  forceOpenHochzeit?: boolean;
  hochzeitDateien?: HochzeitsDateiItem[];
  onAddHochzeitDateien?: (files: File[]) => void;
  onRemoveHochzeitDatei?: (id: string) => void;
  /**
   * Nach einer bewussten Auswahl im Feld (nicht bei der Ableitung) — z. B.
   * für das Popup „Ehepartner erfassen" nach „Verheiratet" (T3).
   */
  onAuswahl?: (familienstand: string) => void;
}

export function FamilienstandSection({
  formFields,
  updateField,
  forceOpenHochzeit = false,
  hochzeitDateien,
  onAddHochzeitDateien,
  onRemoveHochzeitDatei,
  onAuswahl,
}: FamilienstandSectionProps) {
  const s = useRelStyles();

  // Lokaler State für hochgeladene Dateien, falls Aufrufer keine Callbacks übergibt (z. B. Standalone / Tests)
  const [localDateien, setLocalDateien] = React.useState<HochzeitsDateiItem[]>([]);
  const dateien = hochzeitDateien ?? localDateien;

  const rawFamilyStatus = String(formFields.familyStatus ?? "");
  const rawFamilyStatusOther = String(formFields.familyStatusOther ?? "");

  // Initialer Wert ist immer "Ledig", wenn noch kein Wert im Formular steht;
  // wenn eine Eheperson gewählt ist (forceOpenHochzeit), leitet es sich zu "Verheiratet" ab.
  const effectiveStatus = forceOpenHochzeit && !rawFamilyStatus ? "Verheiratet" : (rawFamilyStatus || "Ledig");
  const fam = familienstandAusFormular(effectiveStatus, rawFamilyStatusOther);
  const auswahl = fam.auswahl || "Ledig";

  const isVerheiratet = auswahl === "Verheiratet";

  const [manuellEingeklappt, setManuellEingeklappt] = React.useState(false);
  const [hochzeitOffen, setHochzeitOffen] = React.useState(false);

  // Wenn forceOpenHochzeit aktiv wird (z. B. Eheperson im Haushalt gewählt),
  // heben wir ein manuelles Einklappen auf und öffnen die Sektion.
  React.useEffect(() => {
    if (forceOpenHochzeit) {
      setManuellEingeklappt(false);
      setHochzeitOffen(true);
    }
  }, [forceOpenHochzeit]);

  const hatDaten = Boolean(
    String(formFields.weddingDateCivil ?? "").trim() ||
    String(formFields.weddingDateChurch ?? "").trim() ||
    dateien.length > 0
  );

  const istOffen = isVerheiratet && ((forceOpenHochzeit || hochzeitOffen || hatDaten) && !manuellEingeklappt);

  const handleStatusChange = (v: string) => {
    const felder = familienstandAuswahlInsFormular(v, fam.freitext);
    updateField("familyStatus", felder.familyStatus);
    updateField("familyStatusOther", felder.familyStatusOther);
    if (v === "Verheiratet") {
      setManuellEingeklappt(false);
      setHochzeitOffen(true);
    }
    onAuswahl?.(v);
  };

  const handleFreitextChange = (v: string) => {
    const felder = familienstandFreitextInsFormular(v);
    updateField("familyStatus", felder.familyStatus);
    updateField("familyStatusOther", felder.familyStatusOther);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    if (onAddHochzeitDateien) {
      onAddHochzeitDateien(files);
    } else {
      const items: HochzeitsDateiItem[] = files.map((f) => ({
        id: `hz-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: f.name,
        file: f,
      }));
      setLocalDateien((prev) => [...prev, ...items]);
    }
    // Input leeren für erneute Auswahl derselben Datei
    e.target.value = "";
  };

  const handleRemoveDatei = (id: string) => {
    if (onRemoveHochzeitDatei) {
      onRemoveHochzeitDatei(id);
    } else {
      setLocalDateien((prev) => prev.filter((d) => d.id !== id));
    }
  };

  const fileInputId = `priv-hochzeit-upload-${React.useId().replace(/:/g, "")}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", rowGap: "12px" }}>
      {/* Zeile 1: Familienstand (links) | Sonstiges-Freitext oder leer (rechts) */}
      <FieldRow columns={2}>
        <LabeledSelect
          label="Familienstand"
          name="familyStatus"
          value={auswahl}
          onChange={handleStatusChange}
          options={FAMILIENSTAND_OPTIONS.map((v) => ({ value: v, label: v }))}
          placeholder="Familienstand auswählen"
        />
        {auswahl === FAMILIENSTAND_SONSTIGES ? (
          <LabeledInput
            label="Familienstand (Sonstiges)"
            name="familyStatusOther"
            value={fam.freitext}
            onChange={handleFreitextChange}
            placeholder={FAMILIENSTAND_SONSTIGES_PLATZHALTER}
            maxLength={100}
          />
        ) : (
          <div />
        )}
      </FieldRow>

      {/* Wenn verheiratet: Einklappbarer Bereich für Informationen zur Hochzeit */}
      {isVerheiratet && (
        <>
          {!istOffen ? (
            /* Zeile 2 im eingeklappten Zustand: Button rechts */
            <FieldRow columns={2}>
              <div />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className={s.ghostBtn}
                  onClick={() => {
                    setManuellEingeklappt(false);
                    setHochzeitOffen(true);
                  }}
                >
                  + Informationen zur Hochzeit
                </button>
              </div>
            </FieldRow>
          ) : (
            /* Ausgeklappter Zustand */
            <>
              <div
                style={{
                  marginTop: "6px",
                  marginBottom: "4px",
                  fontFamily: REFINED.fontUI,
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  color: REFINED.ink,
                }}
              >
                Wann geheiratet?
              </div>

              {/* Zeile 4: Hochzeitstage */}
              <FieldRow columns={2}>
                <LabeledInput
                  label="Hochzeitstag (standesamtlich)"
                  name="weddingDateCivil"
                  type="date"
                  value={String(formFields.weddingDateCivil ?? "")}
                  onChange={(v) => updateField("weddingDateCivil", v)}
                />
                <LabeledInput
                  label="Hochzeitstag (kirchlich)"
                  name="weddingDateChurch"
                  type="date"
                  value={String(formFields.weddingDateChurch ?? "")}
                  onChange={(v) => updateField("weddingDateChurch", v)}
                />
              </FieldRow>

              {/* Zeile 5: Einladung, Mitteilung hochladen (links) | Einklappen-Button (rechts) */}
              <div
                style={{
                  marginTop: "8px",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      color: REFINED.ink,
                      marginBottom: "6px",
                      display: "block",
                      fontFamily: REFINED.fontUI,
                    }}
                  >
                    Einladung, Mitteilung hochladen
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {dateien.map((datei) => (
                      <div key={datei.id} className="sza-zeile" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <button
                          type="button"
                          className="sza-btn sza-datei"
                          style={{ maxWidth: "280px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={datei.name}
                        >
                          {DOKUMENT_ICON}
                          <span className="sza-dateiname">{datei.name}</span>
                        </button>
                        <button
                          type="button"
                          className="sza-btn sza-entfernen"
                          onClick={() => handleRemoveDatei(datei.id)}
                          title="Dokument entfernen"
                          aria-label="Dokument entfernen"
                        >
                          {ENTFERNEN_ICON}
                        </button>
                      </div>
                    ))}
                    <div>
                      <input
                        id={fileInputId}
                        type="file"
                        multiple
                        accept="image/*,application/pdf"
                        style={{ display: "none" }}
                        onChange={handleFileInput}
                      />
                      <button
                        type="button"
                        className="sza-btn"
                        onClick={() => elementMitId(fileInputId)?.click()}
                      >
                        {UPLOAD_ICON} {dateien.length > 0 ? "+ Weiteres Dokument hochladen" : "Einladung, Mitteilung hochladen"}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={s.ghostBtn}
                  onClick={() => {
                    setManuellEingeklappt(true);
                    setHochzeitOffen(false);
                  }}
                  title="Informationen zur Hochzeit einklappen"
                >
                  ↑ Informationen zur Hochzeit einklappen
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
